import {
  accountBootstrapClient,
  AccountCommandError,
  type AccountBootstrap,
  type AccountMfaSetup,
} from './accountEnrollment'
import { AUTH_STORAGE_KEYS } from '@/types/auth.types'
import axios from 'axios'

export type ChangePurpose = 'email_change' | 'mfa_rotation' | 'manual_recovery'
export interface ChangeProgress {
  command_id: string
  purpose: ChangePurpose
  status:
    | 'pending'
    | 'email_verified'
    | 'awaiting_review'
    | 'approved'
    | 'mfa_setup'
    | 'complete'
    | 'dormant'
}
function headers() {
  const token =
    sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN) || localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)
  return { Authorization: token ? `Bearer ${token}` : undefined }
}
async function send(path: string, data?: object, authenticated = true) {
  try {
    return data
      ? (
          await accountBootstrapClient.post(path, data, {
            headers: authenticated ? headers() : { Authorization: undefined },
          })
        ).data
      : (
          await accountBootstrapClient.get(path, {
            headers: authenticated ? headers() : { Authorization: undefined },
          })
        ).data
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    throw new AccountCommandError(
      status === 410
        ? 'This request or approval has expired.'
        : status === 429
          ? 'Proof attempts are temporarily locked. Please wait.'
          : status === 409
            ? 'The request or account has changed. Check its status before continuing.'
            : status && status < 500
              ? 'The request could not be accepted. Check your current proofs and access.'
              : 'The result is uncertain. Check the original request status before retrying.',
      !status || status >= 500,
      status
    )
  }
}
export async function changeCommand(
  operation: string,
  body: AccountBootstrap & Record<string, unknown>,
  purpose: ChangePurpose
): Promise<ChangeProgress | AccountMfaSetup> {
  const value = await send(`/auth/client/changes/${operation}`, body, purpose !== 'manual_recovery')
  if (operation === 'mfa-setup') {
    if (
      value &&
      Object.keys(value).length === 3 &&
      /^[A-Z2-7]{32}$/.test(value.secret) &&
      typeof value.qr_code_url === 'string' &&
      value.qr_code_url.startsWith('otpauth://totp/') &&
      Array.isArray(value.backup_codes) &&
      value.backup_codes.length === 10 &&
      value.backup_codes.every(
        (code: unknown) => typeof code === 'string' && /^[A-F0-9]{4}-[A-F0-9]{4}$/.test(code)
      )
    )
      return value
  } else if (
    value &&
    Object.keys(value).length === 3 &&
    value.command_id === body.command_id &&
    value.purpose === purpose &&
    [
      'pending',
      'email_verified',
      'awaiting_review',
      'approved',
      'mfa_setup',
      'complete',
      'dormant',
    ].includes(value.status)
  )
    return value
  throw new AccountCommandError(
    'The server result could not be confirmed. Check the original request status.',
    true
  )
}
export interface RecoveryReviewCase extends ChangeProgress {
  account_id: string | null
  destination_email_verified: boolean
  destination_email: string
  expires_at: string
  reviews: Array<{
    reviewer_id: string
    decision: string
    evidence_reference: string
    evidence_sha256: string
  }>
}
export async function inspectRecovery(reference: string): Promise<RecoveryReviewCase> {
  const value = await send(`/platform/account-recovery/${encodeURIComponent(reference)}`)
  if (
    !value ||
    value.command_id !== reference ||
    value.purpose !== 'manual_recovery' ||
    !Array.isArray(value.reviews)
  )
    throw new AccountCommandError('The recovery case could not be confirmed.', true)
  return value
}
export async function reviewRecovery(body: Record<string, unknown>): Promise<ChangeProgress> {
  const value = await send('/platform/account-recovery/review', body)
  if (!value || value.command_id !== body.command_id || value.purpose !== 'manual_recovery')
    throw new AccountCommandError(
      'The review result could not be confirmed. Inspect the original case.',
      true
    )
  return value
}
